import React, { useState, useEffect } from 'react';
import './DashboardUsers.css';
import '../../pages/PasswordVerification/PasswordVerification.css';
import { supabase } from '../../lib/supabase';
import { logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';
import NotificationModal from '../NotificationModal/NotificationModal';

const NoDataState = ({ type, icon, message, actionText, onAction }) => (
  <div className="no-data-state">
    <div className="no-data-icon">{icon}</div>
    <h3>No {type} Found</h3>
    <p>{message}</p>
    {onAction && (
      <button className="add-first-btn" onClick={onAction}>{actionText}</button>
    )}
  </div>
);

const DashboardUsers = ({ users, schools, dataLoading, onUserAdded }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('alphabet');
  const [filterValue, setFilterValue] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [userFormData, setUserFormData] = useState({
    first_name: '',
    last_name: '',
    school_id: '',
    email: '',
    phone: '',
    role: '',
    grade: '',
    grade_id: '',
    grade_section_id: '',
    language: '',
    is_active: true,
    password: ''
  });
  const [newPassword, setNewPassword] = useState(''); // Separate state for password editing

  // School grades & sections for the currently selected school (same source as DashboardSchool)
  const [schoolGrades, setSchoolGrades] = useState([]);
  const [schoolGradeSections, setSchoolGradeSections] = useState([]);
  const [loadingSchoolGrades, setLoadingSchoolGrades] = useState(false);

  // Teacher multi-grade selection (keys: "grade_id" or "grade_id:section_id")
  const [teacherSelectedGrades, setTeacherSelectedGrades] = useState(new Set());

  const USERS_PER_PAGE = 100;

  // Generate random 6-digit password
  const generateRandomPassword = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Filter and pagination logic
  useEffect(() => {
    let filtered = [...users];

    switch (filterType) {
      case 'alphabet':
        filtered = filtered.sort((a, b) => {
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case 'school':
        if (filterValue) {
          filtered = filtered.filter(user => {
            const userSchool = schools.find(school => school.id === user.school_id);
            return userSchool?.name?.toLowerCase().includes(filterValue.toLowerCase());
          });
        }
        break;
      case 'role':
        if (filterValue) {
          filtered = filtered.filter(user => user.role === filterValue);
        }
        break;
      case 'status':
        if (filterValue) {
          if (filterValue === 'graduated') {
            filtered = filtered.filter(user => user.is_graduated);
          } else if (filterValue === 'active') {
            filtered = filtered.filter(user => user.is_active && !user.is_graduated);
          } else if (filterValue === 'inactive') {
            filtered = filtered.filter(user => !user.is_active && !user.is_graduated);
          }
        }
        break;
      default:
        break;
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [users, filterType, filterValue, schools]);

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setUserFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'radio' ? value === 'true' : value)
      };

      // If role changes, reset grade selections
      if (name === 'role') {
        setTeacherSelectedGrades(new Set());
        newData.grade = '';
        newData.grade_id = '';
        newData.grade_section_id = '';
      }

      return newData;
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Only allow numbers for password field
    const numericValue = value.replace(/\D/g, '').slice(0, 6); // Limit to 6 digits
    setNewPassword(numericValue);
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('dashboard-users-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  // Build list of grade/section options for display (same UX as DashboardSchoolTeacher)
  // Returns array of { key, label, gradeId, sectionId, isSection }
  const buildGradeSectionOptions = () => {
    const options = [];

    (schoolGrades || [])
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .forEach(grade => {
        const sections = (schoolGradeSections || [])
          .filter(s => s.grade_id === grade.id && s.is_active)
          .sort((a, b) => a.order_index - b.order_index);

        if (sections.length > 0) {
          sections.forEach(section => {
            options.push({
              key: `${grade.id}:${section.id}`,
              label: `${grade.name} ${section.name}`,
              gradeId: grade.id,
              sectionId: section.id,
              isSection: true
            });
          });
        } else {
          options.push({
            key: grade.id,
            label: grade.name,
            gradeId: grade.id,
            sectionId: null,
            isSection: false
          });
        }
      });

    return options;
  };

  const parseTeacherGradesToSet = (user) => {
    if (!user) return new Set();
    const selectedKeys = new Set();

    if (user.teachers_grade_ids && Array.isArray(user.teachers_grade_ids) && user.teachers_grade_ids.length > 0) {
      user.teachers_grade_ids.forEach(item => {
        if (typeof item === 'string') {
          selectedKeys.add(item);
        } else if (typeof item === 'object' && item.grade_id) {
          if (item.section_id) selectedKeys.add(`${item.grade_id}:${item.section_id}`);
          else selectedKeys.add(item.grade_id);
        }
      });
      return selectedKeys;
    }

    return selectedKeys;
  };

  const handleTeacherGradeToggle = (key) => {
    setTeacherSelectedGrades(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // If selecting a subsection, remove the parent grade if it exists
        if (key.includes(':')) {
          const [gradeId] = key.split(':');
          next.delete(gradeId);
        } else {
          // If selecting a grade, remove all its subsections
          const gradeId = key;
          Array.from(next).forEach(existingKey => {
            if (existingKey.startsWith(`${gradeId}:`)) next.delete(existingKey);
          });
        }
        next.add(key);
      }
      return next;
    });
  };

  const getSelectedStudentGradeChoiceValue = () => {
    if (!userFormData.grade_id) return '';
    if (userFormData.grade_section_id) return `${userFormData.grade_id}:${userFormData.grade_section_id}`;
    return userFormData.grade_id;
  };

  const handleStudentGradeChoiceChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setUserFormData(prev => ({ ...prev, grade_id: '', grade_section_id: '', grade: '' }));
      return;
    }

    if (value.includes(':')) {
      const [gradeId, sectionId] = value.split(':');
      const grade = schoolGrades.find(g => g.id === gradeId);
      setUserFormData(prev => ({ ...prev, grade_id: gradeId, grade_section_id: sectionId, grade: grade?.name || '' }));
      return;
    }

    const grade = schoolGrades.find(g => g.id === value);
    setUserFormData(prev => ({ ...prev, grade_id: value, grade_section_id: '', grade: grade?.name || '' }));
  };

  // Fetch school grades/sections when the selected school changes
  useEffect(() => {
    const schoolId = userFormData.school_id;
    let cancelled = false;

    async function run() {
      if (!schoolId) {
        setSchoolGrades([]);
        setSchoolGradeSections([]);
        return;
      }

      setLoadingSchoolGrades(true);
      try {
        const { data: gradesData, error: gradesError } = await supabase
          .from('grades')
          .select('*')
          .eq('school_id', schoolId)
          .order('order_index', { ascending: true });
        if (gradesError) throw gradesError;

        if (cancelled) return;
        setSchoolGrades(gradesData || []);

        const gradeIds = (gradesData || []).map(g => g.id);
        if (!gradeIds.length) {
          setSchoolGradeSections([]);
          return;
        }

        const { data: sectionsData, error: sectionsError } = await supabase
          .from('grade_sections')
          .select('*')
          .in('grade_id', gradeIds)
          .order('order_index', { ascending: true });
        if (sectionsError) throw sectionsError;
        if (cancelled) return;

        setSchoolGradeSections(sectionsData || []);
      } catch (err) {

        if (!cancelled) {
          setSchoolGrades([]);
          setSchoolGradeSections([]);
        }
      } finally {
        if (!cancelled) setLoadingSchoolGrades(false);
      }
    }

    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFormData.school_id]);

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const startEditSelectedUser = () => {
    if (!selectedUser) return;
    setUserFormData({
      first_name: selectedUser.first_name || '',
      last_name: selectedUser.last_name || '',
      school_id: selectedUser.school_id || '',
      email: selectedUser.email || '',
      phone: selectedUser.phone || '',
      role: selectedUser.role || '',
      grade: selectedUser.grade || '',
      grade_id: selectedUser.grade_id || '',
      grade_section_id: selectedUser.grade_section_id || '',
      language: selectedUser.language || '',
      is_active: selectedUser.is_active !== false
    });
    setNewPassword(selectedUser.password || ''); // Load existing password when editing

    // Parse teacher multi-grade selection (if applicable)
    if ((selectedUser.role || '').toLowerCase() === 'teacher') {
      setTeacherSelectedGrades(parseTeacherGradesToSet(selectedUser));
    } else {
      setTeacherSelectedGrades(new Set());
    }

    setIsEditing(true);
  };

  // Generate unique user_id based on school_id and incremental number
  const generateUserId = async (schoolId) => {
    try {
      // Get the school's school_id from the schools table
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('school_id')
        .eq('id', schoolId)
        .single();

      if (schoolError) {

        return null;
      }

      const schoolCode = schoolData.school_id;

      // Use the enhanced UserIdGenerator
      return await UserIdGenerator.generateUserId(schoolCode);
    } catch (err) {

      return null;
    }
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Generate unique user_id
      const userId = await generateUserId(userFormData.school_id);
      if (!userId) {
        alert('Error generating user ID. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Generate random 6-digit password
      const randomPassword = generateRandomPassword();

      const role = (userFormData.role || '').toLowerCase();

      // Build payload grade fields based on role
      let gradeText = userFormData.grade || null;
      let gradeId = userFormData.grade_id && String(userFormData.grade_id).trim() !== '' ? userFormData.grade_id : null;
      let gradeSectionId = userFormData.grade_section_id && String(userFormData.grade_section_id).trim() !== '' ? userFormData.grade_section_id : null;
      let teachersGradeIds = null;

      if (role === 'student') {
        // Ensure grade text is the grade name (not section)
        const gradeRow = schoolGrades.find(g => g.id === gradeId);
        gradeText = gradeRow ? gradeRow.name : (gradeText || null);
      } else if (role === 'teacher') {
        // Teachers use teachers_grade_ids array and grade string for backward compatibility
        const selected = Array.from(teacherSelectedGrades).filter(Boolean);
        teachersGradeIds = selected.map(key => {
          if (key.includes(':')) {
            const [gId, sId] = key.split(':');
            return { grade_id: gId, section_id: sId };
          }
          return { grade_id: key, section_id: null };
        });

        const gradeNames = selected
          .map(key => {
            const gId = key.includes(':') ? key.split(':')[0] : key;
            const grade = schoolGrades.find(g => g.id === gId);
            if (!grade) return null;
            if (key.includes(':')) {
              const sId = key.split(':')[1];
              const section = schoolGradeSections.find(s => s.id === sId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          })
          .filter(Boolean);
        gradeText = gradeNames.length ? gradeNames.join(', ') : null;

        // Teachers don't use grade_id / grade_section_id
        gradeId = null;
        gradeSectionId = null;
      } else {
        // Admin/other roles: clear grade fields
        gradeText = null;
        gradeId = null;
        gradeSectionId = null;
        teachersGradeIds = null;
      }

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: userId,
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          school_id: userFormData.school_id || null,
          email: userFormData.email,
          phone: userFormData.phone || null,
          role: userFormData.role || null,
          grade: gradeText,
          grade_id: gradeId,
          grade_section_id: gradeSectionId,
          teachers_grade_ids: teachersGradeIds && teachersGradeIds.length > 0 ? teachersGradeIds : null,
          language: userFormData.language || null,
          is_active: userFormData.is_active,
          password: randomPassword,
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) {

        showNotification('error', 'Error Adding User', error.message);
      } else {
        // Log the activity
        const schoolName = schools.find(s => s.id === userFormData.school_id)?.name || 'Unknown School';
        const userName = `${userFormData.first_name} ${userFormData.last_name}`;
        await logUserActivity.created(userName, schoolName);

        showNotification('success', 'User Added Successfully', `${userName} has been added with ID: ${userId} and Password: ${randomPassword}`);
        setUserFormData({
          first_name: '', last_name: '', school_id: '', email: '', phone: '', role: '', grade: '', grade_id: '', grade_section_id: '', language: '', is_active: true, password: ''
        });
        setTeacherSelectedGrades(new Set());
        setShowAddUserModal(false);
        onUserAdded && onUserAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Adding User', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setIsSubmitting(true);

    try {
      const role = (userFormData.role || '').toLowerCase();

      let gradeText = userFormData.grade || null;
      let gradeId = userFormData.grade_id && String(userFormData.grade_id).trim() !== '' ? userFormData.grade_id : null;
      let gradeSectionId = userFormData.grade_section_id && String(userFormData.grade_section_id).trim() !== '' ? userFormData.grade_section_id : null;
      let teachersGradeIds = null;

      if (role === 'student') {
        const gradeRow = schoolGrades.find(g => g.id === gradeId);
        gradeText = gradeRow ? gradeRow.name : (gradeText || null);
      } else if (role === 'teacher') {
        const selected = Array.from(teacherSelectedGrades).filter(Boolean);
        teachersGradeIds = selected.map(key => {
          if (key.includes(':')) {
            const [gId, sId] = key.split(':');
            return { grade_id: gId, section_id: sId };
          }
          return { grade_id: key, section_id: null };
        });

        const gradeNames = selected
          .map(key => {
            const gId = key.includes(':') ? key.split(':')[0] : key;
            const grade = schoolGrades.find(g => g.id === gId);
            if (!grade) return null;
            if (key.includes(':')) {
              const sId = key.split(':')[1];
              const section = schoolGradeSections.find(s => s.id === sId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          })
          .filter(Boolean);
        gradeText = gradeNames.length ? gradeNames.join(', ') : null;

        gradeId = null;
        gradeSectionId = null;
      } else {
        gradeText = null;
        gradeId = null;
        gradeSectionId = null;
        teachersGradeIds = null;
      }

      // Build update object
      const updateData = {
        first_name: userFormData.first_name,
        last_name: userFormData.last_name,
        school_id: userFormData.school_id || null,
        email: userFormData.email,
        phone: userFormData.phone || null,
        role: userFormData.role || null,
        grade: gradeText,
        grade_id: gradeId,
        grade_section_id: gradeSectionId,
        teachers_grade_ids: teachersGradeIds && teachersGradeIds.length > 0 ? teachersGradeIds : null,
        language: userFormData.language || null,
        is_active: userFormData.is_active,
        updated_at: new Date().toISOString()
      };

      // Graduation rule (students only):
      // If the new grade is NOT the last grade in that school => force is_graduated = null.
      // If the user was graduated before, also force is_active=true so status becomes "Active".
      try {
        if (role === 'student') {
          const schoolIdForGrades = userFormData.school_id || selectedUser?.school_id;

          if (schoolIdForGrades && gradeId) {
            const { data: allGrades, error: gradesError } = await supabase
              .from('grades')
              .select('id, order_index')
              .eq('school_id', schoolIdForGrades)
              .eq('is_active', true)
              .order('order_index', { ascending: false })
              .limit(1);

            const lastGradeId = !gradesError && allGrades && allGrades.length ? allGrades[0].id : null;
            if (lastGradeId && String(gradeId) !== String(lastGradeId)) {
              updateData.is_graduated = null;
              if (selectedUser?.is_graduated) updateData.is_active = true;
            }
          } else {
            // No grade selected means not in last grade => not graduated.
            updateData.is_graduated = null;
            if (selectedUser?.is_graduated) updateData.is_active = true;
          }
        }
      } catch (err) {
        // If anything goes wrong, still prefer clearing graduation when editing grade away from graduation.
        if (role === 'student') {
          updateData.is_graduated = null;
          if (selectedUser?.is_graduated) updateData.is_active = true;
        }

      }

      // Only update password if a new one is provided
      if (newPassword && newPassword.trim() !== '') {
        updateData.password = newPassword.trim();
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', selectedUser.id);

      if (error) {

        showNotification('error', 'Error Updating User', error.message);
      } else {
        // Log the activity
        const userName = `${userFormData.first_name} ${userFormData.last_name}`;
        await logUserActivity.updated(userName);

        const passwordMsg = newPassword && newPassword.trim() !== '' ? ' Password has been updated.' : '';
        showNotification('success', 'User Updated Successfully', `${userName} has been updated successfully.${passwordMsg}`);
        setShowDetailsModal(false);
        setIsEditing(false);
        setNewPassword(''); // Clear password field after successful update
        onUserAdded && onUserAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Updating User', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = () => {
    if (!selectedUser) return;
    setItemToDelete({ type: 'user', data: selectedUser });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', itemToDelete.data.id);

      if (error) {

        showNotification('error', 'Error Deleting User', error.message);
      } else {
        // Log the activity
        const userName = `${itemToDelete.data.first_name} ${itemToDelete.data.last_name}`;
        await logUserActivity.deleted(userName);

        showNotification('success', 'User Deleted Successfully', `${userName} has been deleted successfully`);
        setShowDetailsModal(false);
        setSelectedUser(null);
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        onUserAdded && onUserAdded();
      }
    } catch (err) {

      showNotification('error', 'Error Deleting User', err.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const closeModal = () => {
    setShowAddUserModal(false);
    setUserFormData({
      first_name: '', last_name: '', school_id: '', email: '', phone: '', role: '', grade: '', grade_id: '', grade_section_id: '', language: '', is_active: true, password: ''
    });
    setTeacherSelectedGrades(new Set());
  };

  const getSchoolName = (schoolId) => {
    const school = schools.find(s => s.id === schoolId);
    return school?.name || 'Unknown School';
  };

  return (
    <div className="users-content">
      <div className="content-header">
        <h2>User Management ({filteredUsers.length} of {users.length})</h2>
        <div className="header-actions">
          <button className="filter-button" onClick={() => setShowFilterModal(true)}>
            Filter
          </button>
          {/* <button className="add-button" onClick={() => setShowAddUserModal(true)}>+ Add User</button> */}
        </div>
      </div>

      {dataLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <NoDataState
          type="Users"
          icon=""
          message="No users have been added yet. Add your first user to get started."
          actionText="+ Add First User"
          onAction={null}
        // onAction={() => setShowAddUserModal(true)}
        />
      ) : (
        <>
          <div className="dashboard-users-table">
            <div className="table-header">
              <div className="table-cell header-cell">Name</div>
              <div className="table-cell header-cell">Email</div>
              <div className="table-cell header-cell">School</div>
              <div className="table-cell header-cell">Role</div>
              <div className="table-cell header-cell">Status</div>
            </div>

            {currentUsers.map((user) => (
              <div key={user.id} className="table-row" onClick={() => openUserDetails(user)}>
                <div className="table-cell data-cell">
                  <div className="user-info">
                    <div className="user-avatar">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                      </svg>
                    </div>
                    <div className="user-name-info">
                      <div className="user-name-main">{user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed User'}</div>
                      <div className="user-email-small">{user.email || 'No email'}</div>
                    </div>
                  </div>
                </div>
                <div className="table-cell data-cell">
                  <div className="email-info">
                    <span className="email-text">{user.email || 'No email'}</span>
                  </div>
                </div>
                <div className="table-cell data-cell">
                  <div className="school-info">
                    <span className="school-text">{getSchoolName(user.school_id)}</span>
                  </div>
                </div>
                <div className="table-cell data-cell">
                  <div className="role-info">
                    <span className="role-badge">{user.role || 'No role'}</span>
                  </div>
                </div>
                <div className="table-cell data-cell status-cell">
                  <div className="status-info">
                    <span className={`status-badge ${user.is_graduated
                        ? 'status-graduated'
                        : user.is_active
                          ? 'status-active'
                          : 'status-inactive'
                      }`}>
                      {user.is_graduated ? 'Graduated' : user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({filteredUsers.length} users)
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* User Details / Edit Modal */}
      {showDetailsModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit User' : 'User Details'}</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            {!isEditing ? (
              <div className="user-details">
                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" />
                      </svg>
                    </div>
                    <h4>Personal Information</h4>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">First Name</span>
                      <span className="detail-value">{selectedUser.first_name || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Name</span>
                      <span className="detail-value">{selectedUser.last_name || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Full Name</span>
                      <span className="detail-value">{selectedUser.full_name || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">User ID</span>
                      <span className="detail-value">{selectedUser.user_id || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{selectedUser.email || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Phone</span>
                      <span className="detail-value">{selectedUser.phone || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Role</span>
                      <span className=" role-badge">{selectedUser.role || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Grade</span>
                      <span className="detail-value">{selectedUser.grade || '—'}</span>
                    </div>

                    <div className="detail-item">
                      <span className="detail-label">Password</span>
                      <span className="detail-value password-value">{selectedUser.password || '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">School</span>
                      <span className="detail-value">{getSchoolName(selectedUser.school_id)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`status-badge ${selectedUser.is_graduated
                          ? 'status-graduated'
                          : selectedUser.is_active
                            ? 'status-active'
                            : 'status-inactive'
                        }`}>
                        {selectedUser.is_graduated
                          ? 'Graduated'
                          : selectedUser.is_active
                            ? 'Active'
                            : 'Inactive'}
                      </span>
                    </div>
                    {selectedUser.is_graduated && (
                      <div className="detail-item">
                        <span className="detail-label">Graduation Date</span>
                        <span className="detail-value">
                          {new Date(selectedUser.is_graduated).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="section-divider"></div>

                <div className="details-section">
                  <div className="section-header">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                      </svg>
                    </div>
                    <h4>System Information</h4>
                  </div>
                  <div className="details-grid">
                    {/* <div className="detail-item">
                      <span className="detail-label">User ID</span>
                      <span className="detail-value">{selectedUser.id || '—'}</span>
                    </div> */}
                    <div className="detail-item">
                      <span className="detail-label">Created</span>
                      <span className="detail-value">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated</span>
                      <span className="detail-value">{selectedUser.updated_at ? new Date(selectedUser.updated_at).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                </div>

                {selectedUser.external_ids && Object.keys(selectedUser.external_ids).length > 0 && (
                  <>
                    <div className="section-divider"></div>
                    <div className="details-section">
                      <div className="section-header">
                        <div className="section-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                        </div>
                        <h4>External IDs</h4>
                      </div>
                      <div className="details-grid">
                        {Object.entries(selectedUser.external_ids).map(([key, value]) => (
                          <div key={key} className="detail-item">
                            <span className="detail-label">{key}</span>
                            <span className="detail-value">{value || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {selectedUser.profile && Object.keys(selectedUser.profile).length > 0 && (
                  <>
                    <div className="section-divider"></div>
                    <div className="details-section">
                      <div className="section-header">
                        <div className="section-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z" />
                          </svg>
                        </div>
                        <h4>Profile Information</h4>
                      </div>
                      <div className="details-grid">
                        {Object.entries(selectedUser.profile).map(([key, value]) => (
                          <div key={key} className="detail-item">
                            <span className="detail-label">{key}</span>
                            <span className="detail-value">{value || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="button" className="user-cancel-btn" onClick={handleDeleteUser}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                    </svg>
                    Delete User
                  </button>
                  <button type="button" className="user-submit-btn" onClick={startEditSelectedUser}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.71 7.04C21.1 6.65 21.1 6 20.71 5.63L18.37 3.29C18 2.9 17.35 2.9 16.96 3.29L15.12 5.12L18.87 8.87M3 17.25V21H6.75L17.81 9.93L14.06 6.18L3 17.25Z" />
                    </svg>
                    Edit User
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdateUser} className="user-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name *</label>
                    <input type="text" id="first_name" name="first_name" value={userFormData.first_name} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="last_name">Last Name *</label>
                    <input type="text" id="last_name" name="last_name" value={userFormData.last_name} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="school_id">School Name *</label>
                    <select id="school_id" name="school_id" value={userFormData.school_id} onChange={handleInputChange} required>
                      <option value="">Select school</option>
                      {schools.map(school => (
                        <option key={school.id} value={school.id}>{school.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input type="email" id="email" name="email" value={userFormData.email} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={userFormData.phone} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="role">Role *</label>
                    <select id="role" name="role" value={userFormData.role} onChange={handleInputChange} required>
                      <option value="">Select role</option>
                      <option value="admin">Admin</option>
                      <option value="teacher">Teacher</option>
                      <option value="student">Student</option>
                      {/* <option value="parent">Parent</option>
                      <option value="staff">Staff</option> */}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  {(String(userFormData.role || '').toLowerCase() === 'student' || String(userFormData.role || '').toLowerCase() === 'teacher') && (
                    <div className="form-group">
                      <label>
                        Grade{String(userFormData.role || '').toLowerCase() === 'student' ? ' *' : ''}
                      </label>

                      {!userFormData.school_id ? (
                        <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                          Select a school first to load its grades.
                        </p>
                      ) : loadingSchoolGrades ? (
                        <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                          Loading grades...
                        </p>
                      ) : String(userFormData.role || '').toLowerCase() === 'student' ? (
                        <select
                          name="grade_choice"
                          value={getSelectedStudentGradeChoiceValue()}
                          onChange={handleStudentGradeChoiceChange}
                          required
                        >
                          <option value="">Select grade</option>
                          {buildGradeSectionOptions().map((opt) => (
                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        schoolGrades.length > 0 ? (
                          <div className="grade-level-selection" style={{ maxHeight: 220 }}>
                            {buildGradeSectionOptions().map((opt) => {
                              const isChecked = teacherSelectedGrades.has(opt.key);
                              return (
                                <label key={opt.key} className="grade-level-option">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleTeacherGradeToggle(opt.key)}
                                  />
                                  <span>{opt.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                            No grades available for this school. Add grades in the School dashboard first.
                          </p>
                        )
                      )}
                    </div>
                  )}

                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="new_password">Change Password</label>
                    {/* Modern 6-square password UI */}
                    <div className="dashboard-users-password-squares-container">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className={`dashboard-users-password-square ${newPassword.length > index ? 'filled' : ''} ${newPassword.length === index ? 'active' : ''}`}
                          onClick={handlePasswordSquareClick}
                        >
                          {newPassword[index] || ''}
                        </div>
                      ))}
                    </div>
                    {/* Hidden input that actually controls the password value */}
                    <input
                      type="password"
                      id="dashboard-users-password-input"
                      name="new_password"
                      value={newPassword}
                      onChange={handlePasswordChange}
                      className="dashboard-users-password-hidden-input"
                      placeholder="Enter 6-digit password (numbers only)"
                      maxLength="6"
                      autoComplete="new-password"
                    />
                    <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                      Password must be 6 digits (numbers only). Current password is shown. Edit to change it, or leave as-is to keep the same password.
                    </small>
                  </div>
                </div>

                <div className="form-divider"></div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={userFormData.is_active === true}
                        onChange={(e) => setUserFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      />
                      <span>Active</span>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="user-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  <button type="submit" className="user-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add User Modal - Commented Out */}
      {false && showAddUserModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmitUser} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="add_first_name">First Name *</label>
                  <input type="text" id="add_first_name" name="first_name" value={userFormData.first_name} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="add_last_name">Last Name *</label>
                  <input type="text" id="add_last_name" name="last_name" value={userFormData.last_name} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="add_school_id">School Name *</label>
                  <select id="add_school_id" name="school_id" value={userFormData.school_id} onChange={handleInputChange} required>
                    <option value="">Select school</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>{school.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="add_email">Email Address *</label>
                  <input type="email" id="add_email" name="email" value={userFormData.email} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="add_phone">Phone Number</label>
                  <input type="tel" id="add_phone" name="phone" value={userFormData.phone} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="add_role">Role *</label>
                  <select id="add_role" name="role" value={userFormData.role} onChange={handleInputChange} required>
                    <option value="">Select role</option>
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="student">Student</option>
                    {/* <option value="parent">Parent</option>
                    <option value="staff">Staff</option> */}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Grade</label>

                  {(String(userFormData.role || '').toLowerCase() === 'student' || String(userFormData.role || '').toLowerCase() === 'teacher') ? (
                    !userFormData.school_id ? (
                      <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                        Select a school first to load its grades.
                      </p>
                    ) : loadingSchoolGrades ? (
                      <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                        Loading grades...
                      </p>
                    ) : String(userFormData.role || '').toLowerCase() === 'student' ? (
                      <select
                        name="grade_choice"
                        value={getSelectedStudentGradeChoiceValue()}
                        onChange={handleStudentGradeChoiceChange}
                        required
                      >
                        <option value="">Select grade</option>
                        {buildGradeSectionOptions().map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      schoolGrades.length > 0 ? (
                        <div className="grade-level-selection" style={{ maxHeight: 220 }}>
                          {buildGradeSectionOptions().map((opt) => {
                            const isChecked = teacherSelectedGrades.has(opt.key);
                            return (
                              <label key={opt.key} className="grade-level-option">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleTeacherGradeToggle(opt.key)}
                                />
                                <span>{opt.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                          No grades available for this school. Add grades in the School dashboard first.
                        </p>
                      )
                    )
                  ) : (
                    <p style={{ color: '#666', fontSize: '0.875rem', margin: 0 }}>
                      Grade not required for this role.
                    </p>
                  )}
                </div>

              </div>

              <div className="form-divider"></div>

              <div className="form-row">
                <div className="form-group">
                  <label>User Status *</label>
                  <div className="user-radio-group">
                    <label className="radio-option">
                      <input type="radio" name="is_active" value="true" checked={userFormData.is_active === true} onChange={handleInputChange} />
                      <span>Active</span>
                    </label>
                    <label className="radio-option">
                      <input type="radio" name="is_active" value="false" checked={userFormData.is_active === false} onChange={handleInputChange} />
                      <span>Inactive</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="user-cancel-btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="user-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Adding User...' : 'Add User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content filter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Filter Users</h2>
              <button className="modal-close" onClick={() => setShowFilterModal(false)}>×</button>
            </div>

            <div className="filter-content">
              <div className="filter-section">
                <h4>Filter By</h4>
                <div className="filter-options">
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="alphabet" checked={filterType === 'alphabet'} onChange={(e) => setFilterType(e.target.value)} />
                    Alphabetical Order
                  </label>
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="school" checked={filterType === 'school'} onChange={(e) => setFilterType(e.target.value)} />
                    School
                  </label>
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="role" checked={filterType === 'role'} onChange={(e) => setFilterType(e.target.value)} />
                    Role
                  </label>
                  <label className="filter-option">
                    <input type="radio" name="filterType" value="status" checked={filterType === 'status'} onChange={(e) => setFilterType(e.target.value)} />
                    Status
                  </label>
                </div>
              </div>

              {filterType === 'school' && (
                <div className="filter-section">
                  <h4>School Name</h4>
                  <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Schools</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.name}>{school.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {filterType === 'role' && (
                <div className="filter-section">
                  <h4>Role</h4>
                  <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="teacher">Teacher</option>
                    <option value="student">Student</option>
                    {/* <option value="parent">Parent</option>
                    <option value="staff">Staff</option> */}
                  </select>
                </div>
              )}

              {filterType === 'status' && (
                <div className="filter-section">
                  <h4>Status</h4>
                  <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="graduated">Graduated</option>
                  </select>
                </div>
              )}

              <div className="filter-actions">
                <button className="user-cancel-btn" onClick={() => {
                  setFilterType('alphabet');
                  setFilterValue('');
                  setShowFilterModal(false);
                }}>Clear Filters</button>
                <button className="user-submit-btn" onClick={() => setShowFilterModal(false)}>Apply Filters</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && itemToDelete && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className=" confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <div className="confirmation-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
              </div>
              <h2>Delete User</h2>
            </div>

            <div className="confirmation-content">
              <p className="confirmation-message">
                Are you sure you want to delete <strong>"{itemToDelete.data.first_name} {itemToDelete.data.last_name}"</strong>?
              </p>
              <p className="confirmation-warning">
                This action cannot be undone and will permanently remove all associated data.
              </p>
            </div>

            <div className="confirmation-actions">
              <button className="cancel-delete-btn" onClick={cancelDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
                </svg>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4H15.5L14.5 3H9.5L8.5 4H5V6H19M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" />
                </svg>
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        autoClose={true}
        autoCloseDelay={4000}
      />
    </div>
  );
};

export default DashboardUsers;


